// Envio do acompanhamento acadêmico à família: prévia, mensagem editável, canais e histórico.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail, MessageCircle, Send } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { C, StatusBadge } from '@/design-system/dna';

const CHANNELS = [['whatsapp', 'WhatsApp', MessageCircle], ['email', 'E-mail', Mail], ['ambos', 'Ambos', Send]];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

const openPdf = async (url, params) => {
  const { data } = await api.get(url, { params, responseType: 'blob' });
  window.open(URL.createObjectURL(data), '_blank');
};

export function SendFamilyDialog({ student, year, scope, endpoint, onClose }) {
  const [prev, setPrev] = useState(null);
  const [sel, setSel] = useState([]);
  const [channel, setChannel] = useState('ambos');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const open = !!student;

  useEffect(() => {
    if (!student) return;
    setPrev(null);
    api.get(`/central-notas/faltantes-aprovacao/envio/${student.student_id}/preview`, { params: { year, scope } })
      .then(({ data }) => { setPrev(data); setMessage(data.message); setSel(data.guardians.filter((g) => g.default).map((g) => g.responsible_id)); })
      .catch((e) => { toast.error(e?.response?.data?.detail || 'Não foi possível abrir a prévia.'); onClose(); });
  }, [student, year, scope, onClose]);

  const send = async () => {
    setSending(true);
    try {
      const channels = channel === 'ambos' ? ['whatsapp', 'email'] : [channel];
      const { data } = await api.post('/central-notas/faltantes-aprovacao/envio', { student_id: student.student_id, year, scope, responsible_ids: sel, channels, message });
      setPrev((p) => ({ ...p, history: data.history }));
      if (data.sent && !data.failed) toast.success(`Enviado com sucesso (${data.sent}).`);
      else if (data.sent) toast.warning(`${data.sent} enviado(s), ${data.failed} com falha — veja o histórico.`);
      else toast.error('Nenhum envio concluído — veja o motivo no histórico.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao enviar.'); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[760px] w-[96vw] max-h-[92vh] overflow-y-auto" data-testid="send-family-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold" style={{ color: C.ink }}>Enviar à família · {student?.student_name}</DialogTitle>
          <DialogDescription>Prévia do envio com o PDF do acompanhamento acadêmico anexado.</DialogDescription>
        </DialogHeader>
        {!prev ? <p className="flex items-center gap-2 py-8 text-sm" style={{ color: C.muted }}><Loader2 className="h-4 w-4 animate-spin" /> Preparando prévia…</p> : (
          <div className="flex flex-col gap-4">
            <div>
              <p className="dna-kicker mb-2">Responsáveis</p>
              {!prev.guardians.length && <p className="text-sm" style={{ color: C.muted }} data-testid="send-no-guardians">Nenhum responsável vinculado a este aluno.</p>}
              <div className="grid gap-2">
                {prev.guardians.map((g) => (
                  <label key={g.responsible_id} className="flex cursor-pointer items-start gap-3 rounded-2xl border p-3" style={{ borderColor: C.line }} data-testid={`send-guardian-${g.responsible_id}`}>
                    <Checkbox checked={sel.includes(g.responsible_id)} onCheckedChange={() => setSel((s) => (s.includes(g.responsible_id) ? s.filter((x) => x !== g.responsible_id) : [...s, g.responsible_id]))} data-testid={`send-guardian-check-${g.responsible_id}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-bold" style={{ color: C.ink }}>{g.name} <span className="font-normal" style={{ color: C.muted }}>{g.relation ? `· ${g.relation}` : ''}{g.pedagogical ? ' · Pedagógico' : ''}</span></span>
                      <span className="block text-[12px]" style={{ color: g.whatsapp ? C.muted : C.orange }}>WhatsApp: {g.whatsapp || 'não cadastrado'}</span>
                      <span className="block text-[12px]" style={{ color: g.email ? C.muted : C.orange }}>E-mail: {g.email || 'não cadastrado'}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl p-3" style={{ background: C.paper }}>
              <span className="text-[13px] font-semibold" style={{ color: C.ink }} data-testid="send-pdf-name">PDF anexado: acompanhamento de {student.student_name.split(' ')[0]} · {year}</span>
              <Button variant="outline" size="sm" onClick={() => openPdf(endpoint, { year, student_id: student.student_id, format: 'pdf' })} data-testid="send-pdf-preview">Ver PDF</Button>
            </div>
            <div>
              <p className="dna-kicker mb-2">Mensagem (editável)</p>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={11} className="text-[13px]" data-testid="send-message" />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-2" data-testid="send-channels">
                {CHANNELS.map(([k, label, Icon]) => (
                  <button key={k} type="button" onClick={() => setChannel(k)} aria-pressed={channel === k} data-testid={`send-channel-${k}`}
                    className="inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-colors"
                    style={channel === k ? { background: C.aubergine, color: '#fff', borderColor: C.aubergine } : { borderColor: C.line, color: C.ink }}>
                    <Icon className="h-4 w-4" />{label}
                  </button>
                ))}
              </div>
              <Button onClick={send} disabled={sending || !sel.length || message.trim().length < 10} className="h-11 px-6" data-testid="send-submit">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar agora
              </Button>
            </div>
            <div>
              <p className="dna-kicker mb-2">Histórico de envios</p>
              {!prev.history.length ? <p className="text-[12.5px]" style={{ color: C.muted }} data-testid="send-history-empty">Nenhum envio registrado para este aluno.</p> : (
                <ul className="grid gap-1.5" data-testid="send-history">
                  {prev.history.map((h) => (
                    <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2 text-[12px]" style={{ borderColor: C.line }}>
                      <span style={{ color: C.ink }}>{fmt(h.created_at)} · <b>{h.responsible_name}</b> · {h.channel === 'email' ? 'E-mail' : 'WhatsApp'} ({h.to || '—'}) · por {h.user_name}</span>
                      <span className="flex items-center gap-2">
                        <button type="button" className="font-semibold" style={{ color: C.purple }} onClick={() => openPdf(`/central-notas/faltantes-aprovacao/envio/pdf/${h.pdf_id}`)} data-testid={`send-history-pdf-${h.id}`}>PDF</button>
                        <StatusBadge tone={h.status === 'sent' ? 'success' : 'danger'}>{h.status === 'sent' ? 'Enviado' : `Falha${h.error ? `: ${h.error}` : ''}`}</StatusBadge>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
